import PickTimeCalendar from './PickTimeCalendar';
import PickTimeLoading from './PickTimeLoading';
import PickTimeNoTimesAtAll from './PickTimeNoTimesAtAll';
import PickTimeViewBar from './components/PickTimeViewBar/PickTimeViewBar';
import PickTimeWeekView from './PickTimeWeekView';
import { PickTimeProps } from './PickTime.types';

/**
 * The pick-time container view (MPA-2665).
 *
 * Switches between the four content views the epic built — loading, the week
 * grid, the month calendar and the terminal no-times state. Presentational: it
 * calls no hooks, so `usePickTime` owns every decision and the stories can drive
 * all four states from plain props.
 */
const PickTime = ({
  isLoading,
  isNoTimesAtAll,
  isMonthView,
  isNarrow,
  variant,
  canToggleMonthView,
  regularTimesUrl,
  weekRangeTitle,
  weekModel,
  calendarData,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
  onJumpToNextAvailable,
  onToggleMonthView,
  onSelectDay,
  onSelectSlot,
  onChooseAnotherService,
  onChangeEmployee,
  onOpenWaitlist,
}: PickTimeProps) => {
  if (isLoading) {
    return <PickTimeLoading title={weekRangeTitle} isNarrow={isNarrow} />;
  }

  if (isNoTimesAtAll) {
    return (
      <PickTimeNoTimesAtAll
        title={weekRangeTitle}
        isNarrow={isNarrow}
        onChooseAnotherService={onChooseAnotherService}
        onChangeEmployee={onChangeEmployee}
        onJoinWaitlist={onOpenWaitlist}
        regularTimesUrl={regularTimesUrl}
      />
    );
  }

  // No waitlist props: the calendar ships without a no-times treatment, so it has no
  // state the entry could belong to, and recovery is the week view's job. Its bar keeps
  // the seam for the day that changes.
  if (isMonthView) {
    return (
      <PickTimeCalendar
        title={weekRangeTitle}
        calendarData={calendarData}
        isNarrow={isNarrow}
        monthViewOn
        onSelectDay={onSelectDay}
        onToggleMonthView={onToggleMonthView}
      />
    );
  }

  // The bar is a sibling of the week grid (wrapped so it can shrink), but
  // PickTimeCalendar renders one internally — so it appears exactly once in
  // either mode. The asymmetry is deliberate.
  //
  // Withholding the bar is the whole of the month-view suppression: it is the only
  // affordance that sets `isMonthView`, so the branch above needs no matching guard.
  //
  // It also carries no waitlist link: in week mode the entry belongs to the week
  // view's empty state, which owns the only moment the waitlist is the right answer.
  return (
    <div className="flex h-full w-full flex-col">
      <div className="min-h-0 flex-1">
        <PickTimeWeekView
          model={weekModel}
          isNarrow={isNarrow}
          variant={variant}
          canGoPrev={canGoPrev}
          canGoNext={canGoNext}
          onPrev={onPrev}
          onNext={onNext}
          onSelectSlot={onSelectSlot}
          onJumpToNextAvailable={onJumpToNextAvailable}
          // Derived rather than a second input, and with no emptiness conjunct: the
          // view renders this button only inside its own empty-week branch, so having
          // somewhere to send the visitor is the whole of the decision here.
          showWaitlist={Boolean(onOpenWaitlist)}
          onOpenWaitlist={onOpenWaitlist}
          regularTimesUrl={regularTimesUrl}
        />
      </div>
      {canToggleMonthView && (
        <PickTimeViewBar monthViewOn={false} isNarrow={isNarrow} onToggleMonthView={onToggleMonthView} />
      )}
    </div>
  );
};

export default PickTime;
